import React, { useEffect, useState } from "react"
import { Box, Typography, useTheme } from "@mui/material"
import { Formik } from "formik"
import * as Yup from "yup"
import CommonModal from "components/CommonModal"
import { useDispatch, useSelector } from "react-redux"
import {
  getCMSRestaurantGroupDetailAction,
  createCMSRestaurantGroupAction,
  updateCMSRestaurantGroupAction
} from "redux/superAdmin/actions"
import RestaurantGroupFormContent from "./restaurantGroupForm"
import AppButton from "components/StyledComponents/AppButton"

const validationSchema = Yup.object({
  name: Yup.string().required("Name is required").max(256),
  email: Yup.string().email("Invalid email").required("Email is required")
})

const RestaurantGroupFormModal = ({ open, onClose, groupId }) => {
  const dispatch = useDispatch()
  const isEdit = Boolean(groupId)
  const theme = useTheme()
  const [iconFile, setIconFile] = useState(null)
  const [iconMediaId, setIconMediaId] = useState(null)
  const [iconUploading, setIconUploading] = useState(false)

  const { cmsRestaurantGroupDetail } = useSelector((store) => store?.superAdmin ?? {})

  useEffect(() => {
    if (!open) return
    setIconFile(null)
    setIconMediaId(null)
    setIconUploading(false)
    if (isEdit) {
      dispatch(getCMSRestaurantGroupDetailAction({ id: groupId }))
    }
  }, [open, groupId])

  useEffect(() => {
    if (
      isEdit &&
      cmsRestaurantGroupDetail &&
      String(cmsRestaurantGroupDetail.id) === String(groupId)
    ) {
      if (cmsRestaurantGroupDetail.icon_url) {
        setIconFile({ url: cmsRestaurantGroupDetail.icon_url })
      }
    }
  }, [cmsRestaurantGroupDetail])

  function handleSubmit(values) {
    const payload = {
      name: values.name,
      email: values.email
    }
    if (iconMediaId) {
      payload.icon_id = iconMediaId
    }
    if (isEdit) {
      dispatch(updateCMSRestaurantGroupAction({ id: groupId, payload, onSuccess: onClose }))
    } else {
      dispatch(createCMSRestaurantGroupAction({ payload, onSuccess: onClose }))
    }
  }

  const ActionButtons = ({ iconUploading, formik, isEdit }) => {
    return (
      <Box className="d-flex align-items-center justify-content-center mb-3">
        <AppButton
          onClick={formik.handleSubmit}
          disabled={iconUploading}
          sx={{ padding: "14px 20px" }}>
          {isEdit ? "Update Restaurant Group" : "Create Restaurant Group"}
        </AppButton>
      </Box>
    )
  }

  const initialValues = {
    name: isEdit ? cmsRestaurantGroupDetail?.name || "" : "",
    email: isEdit ? cmsRestaurantGroupDetail?.email || "" : ""
  }

  return (
    <Formik
      enableReinitialize={true}
      initialValues={initialValues}
      validationSchema={validationSchema}
      onSubmit={handleSubmit}>
      {(formik) => (
        <CommonModal
          open={open}
          onClose={onClose}
          maxWidth="md"
          actionButtons={
            <ActionButtons
              theme={theme}
              iconUploading={iconUploading}
              formik={formik}
              onClose={onClose}
              isEdit={isEdit}
            />
          }>
          <Box className="d-flex align-items-center justify-content-center">
            <Typography variant="h6" sx={{ fontWeight: 600 }}>
              {isEdit ? "Edit Restaurant Group" : "Add Restaurant Group"}
            </Typography>
          </Box>

          <RestaurantGroupFormContent
            formik={formik}
            isEdit={isEdit}
            iconFile={iconFile}
            setIconFile={setIconFile}
            setIconMediaId={setIconMediaId}
            iconUploading={iconUploading}
            setIconUploading={setIconUploading}
            onClose={onClose}
          />
        </CommonModal>
      )}
    </Formik>
  )
}

export default RestaurantGroupFormModal
